import { ORIGIN_ENDPOINT } from '@/env';
import {
  GetAtRegulationConfig,
  GetAtRegulationConfig_atRegulationConfig,
} from '@/features/app-heating-system/views/tree-node/__generated__/GetAtRegulationConfig';
import { ADD_TOAST_MESSAGES, RootStore } from '@/features/core/store';
import { HeatingSystemSummary } from '@/features/domain-ui/heating-system-list-table/model';
import { keycloak } from '@/keycloak';
import { DollarApollo } from 'vue-apollo/types/vue-apollo';
import { ActionContext, GetterTree, Module, MutationTree } from 'vuex';
import getAtRegulationConfigQuery from '@/features/app-heating-system/views/tree-node/get-at-regulation-config.gql';
import activateAtRegulationConfigMutation from '@/features/app-heating-system/views/tree-node/activate-at-regulation-config.gql';
import deActivateAtRegulationConfigMutation from '@/features/app-heating-system/views/tree-node/deactivate-at-regulation-config.gql';
import { AtRegulationConfigInput, DeleteAtRegulationConfigInput, HeatingControlType } from '@/types/iot-portal';
import {
  ActivateAtRegulation,
  ActivateAtRegulation_activateAtRegulation,
  ActivateAtRegulationVariables,
} from '@/features/app-heating-system/views/tree-node/__generated__/ActivateAtRegulation';
import {
  DeactivateAtRegulation,
  DeactivateAtRegulationVariables,
} from '@/features/app-heating-system/views/tree-node/__generated__/DeactivateAtRegulation';
import {
  GetAtRegulationConfigStatuses,
  GetAtRegulationConfigStatuses_checkAtRegulationConfigStatuses,
} from '@/features/app-heating-system/views/tree-node/__generated__/GetAtRegulationConfigStatuses';
import getATRegulationConfigStatusesQuery from '@/features/app-heating-system/views/tree-node/get-at-regulation-statuses.gql';

export type HeatingSystem = HeatingSystemSummary & {
  active: boolean;
  metadata?: HeatingSystemMetadata;
};
export type HeatingSystemComponent = {
  label: string;
  value: string;
  active: boolean;
  type: string;
  groupName: string;
  hasEditableRegister: boolean;
};
export type HeatingSystemMetadata = {
  // Kessel
  boiler: {
    condensing: boolean | null;
    wall_mounted: boolean | null;
    return_temp_uplift: boolean | null;
    safety_temp_guard: number | null;
    safety_temp_limiter: number | null;
    cascade: boolean | null;
    is_modulating: boolean | null;
    has_own_pump: boolean | null;
    has_regulating_valve: boolean | null;
    has_check_valve: boolean | null;
    system_separation: boolean | null;
    power_max_out: number | null;
    power_min_out: number | null;
    has_hydraulic_shunt: boolean | null;
  };
  // Warmwasserbereitung
  waterHeating: {
    type_of_pwh: string | null;
    potable_water_target_temp: number | null;
    potable_water_temperature_tolerance: number | null;
    circulation_target_temp: number | null;
    circulation_temperature_tolerance: number | null;
  };
  // Pufferspeicher
  buffer: {
    type: BufferTypes | null;
    upper_limit: number | null;
    lower_limit: number | null;
    prim_port_energy_input: boolean | null;
    sec_port_energy_input: boolean | null;
    tert_port_energy_input: boolean | null;
    quart_port_energy_input: boolean | null;
    two_sensors_controlled: boolean | null;
  };
  // Heizkreis
  heatingCircuit: {
    control_parameter: {
      type: HeatingCircuitTypes | null;
      summer_shutdown_temp: number | null;
      nightly_reduced_temperature: number | null;
      minimum_temperature: number | null;
      maximum_temperature: number | null;
      supply_potable_water_heating: boolean | null;
      room_target_temperature?: number | null;
      offset?: number | null;
      slope?: number | null;
      target_temperature?: number | null;
      env_flow_temp_points?: number[][] | null;
    };
    radiator_type: HeatingCircuitRadiatorTypes | null;
    heating_circuit_building_year: number | null;
    temperature_control_hardware: HeatingCircuitTemperatureControlHardware | null;
    execute_oscillation_analysis: boolean | null;
  };
  // Fernwärme
  teleHeating: {
    system_separation: boolean | null;
    teleheating_type: TeleHeatingType | null;
    safety_temp_guard: number | null;
    safety_temp_limiter: number | null;
    temperature_control_type: string | null;
    flow_temperature_control_parameter: TemperatureControlParameter;
    return_temperature_control_parameter: TemperatureControlParameter;
  };
  // Wärmepumpe Primäre
  warmPumpPrimary: {
    energy_source_type: WarmPumpEnergySource | null;
    heat_sink_type: WarmPumpHeatSinkType | null;
    supply_potable_water_heating: boolean | null;
    cop_at_design: number | null;
    has_buffer: boolean | null;
    has_heating_element: boolean | null;
    cooling_mode_available: boolean | null;
    cascade: boolean | null;
  };
  // Wärmepumpe Sekundäre
  warmPumpSecondary: {
    energy_source_type: WarmPumpEnergySource | null;
    heat_sink_type: WarmPumpHeatSinkType | null;
    supply_potable_water_heating: boolean | null;
    cop_at_design: number | null;
    has_buffer: boolean | null;
    has_heating_element: boolean | null;
    cooling_mode_available: boolean | null;
    cascade: boolean | null;
  };

  // Wärmetauscher Primäre
  heatExchangerPrimary: {
    terminal_temperature_difference: number | null;
    design_power: number | null;
    flow_direction: HeatExchangerFlowDirection | null;
  };

  // Wärmetauscher Sekundäre
  heatExchangerSecondary: {
    terminal_temperature_difference: number | null;
    design_power: number | null;
    flow_direction: HeatExchangerFlowDirection | null;
  };
  // BHKW (Combined Heat and Power)
  chp: {
    th_power_max_out: number | null;
    el_power_max_out: number | null;
    power_gt_2mw: boolean | null;
  };
  // Heating Control Outside Temperature
  heatingControlOutsideTemperature: {
    yearOfConstruction: number | null;
    type: HeatingControlType | null;
    heatedArea: number | null;
    forecastHours: number | null;
  };
};

export type HeatingSystemMetadataKey = HeatingSystemMetadata[keyof HeatingSystemMetadata];

export type TemperatureControlParameter = {
  type: string | null;
  target_temperature?: number | null;
  pointed_curve_parameter?: number[][] | null;
};

export enum WarmPumpEnergySource {
  AIR = 'air',
  WATER = 'water',
  BRINE = 'brine',
}

export enum WarmPumpHeatSinkType {
  AIR = 'air',
  WATER = 'water',
}

export enum HeatExchangerFlowDirection {
  COUNTER_CURRENT = 'counter_current',
  DIRECT_CURRENT = 'direct_current',
}

export enum TeleHeatingType {
  SINGLE = 'single',
  DUAL = 'dual',
  DIRECT = 'direct',
}

export enum WaterHeatingTypes {
  FRESH_WATER_STATION = 'fresh_water_station',
  BUFFER_CHARGING_SYSTEM = 'buffer_charging_system',
  BUFFER_HEATING_COIL_SYSTEM = 'buffer_heating_coil_system',
}

export enum BufferTypes {
  SOLAR = 'solar',
  HEAT_ENERGY_STORAGE = 'heat_energy_storage',
  POTABLE_WATER = 'potable_water',
}

export enum HeatingCircuitTypes {
  AUTOMATIC = 'automatic',
  PARAMETERIZED_CURVE = 'parameterized_curve',
  POINTED_CURVE = 'pointed_curve',
  CONSTANT_TEMPERATURE = 'constant_temperature',
}

export enum HeatingCircuitTemperatureControlHardware {
  FLOW_CTRL_VALVE = 'flow_ctrl_valve',
  DIFF_PRESSURE_CTRL = 'diff_pressure_ctrl',
  FLOW_DIVERTED = 'flow_diverted',
  INJECTION_UNIT = 'injection_unit',
  INLINE_VALVE = 'inline_valve',
  DOUBLE_ADMIX = 'double_admix',
  SINGLE_ADMIX = 'single_admix',
  UNMIXED = 'unmixed',
}

export enum HeatingCircuitRadiatorTypes {
  CONVENTIONAL_RADITOR = 'conventional_radiator',
  VENTILATION = 'ventilation',
  UNDERFLOOR_HEATING = 'underfloor_heating',
}

export type MetadataPayload = {
  spotId: string;
  heatingSystemId: string;
  componentType: string;
  metadata: HeatingSystemMetadataKey;
};

export type CreateConnectionPayload = {
  customerId: string;
};

export type UpdateConnectionPayload = {
  customerId: string;
  enabled: boolean;
  consent: boolean;
  treeNodeIds: string[];
};

export type ApolloClientParams = {
  apolloClient: DollarApollo<Vue>;
};

export type MetadataKeys = HeatingSystemMetadata['boiler'] &
  HeatingSystemMetadata['waterHeating'] &
  HeatingSystemMetadata['buffer'] &
  HeatingSystemMetadata['heatingCircuit'] &
  HeatingSystemMetadata['teleHeating'] &
  HeatingSystemMetadata['warmPumpPrimary'] &
  HeatingSystemMetadata['warmPumpSecondary'] &
  HeatingSystemMetadata['heatExchangerPrimary'] &
  HeatingSystemMetadata['heatExchangerSecondary'];

export type HeatingSystemMetadataResponse = {
  componentType: string;
  heatingsystemId: string;
  metadata: JSONObject;
  spotId: string;
};

export type HeatingSystemsMetadataState = {
  openModal: boolean;
  openApplySettings: boolean;
  openAuditView: boolean;
  heatingSystems: HeatingSystem[];
  heatingSystemsComponents: HeatingSystemComponent[];
  formData: HeatingSystemMetadata | null;
  heatingSystemsMetadata: HeatingSystemMetadataResponse[];
  activeHeatingSystemAtRegulationConfig: GetAtRegulationConfig_atRegulationConfig | null;
  idsToApplySettings: string[];
  atRegulationConfigStatusPerComponent: Record<string, boolean>;
};

const checkPayload = (payload: MetadataPayload): MetadataPayload => {
  if (payload.componentType === 'WARM_HEATING') {
    payload.componentType = 'POTABLE_WATER_HEATING';
  }

  return payload;
};

const state: HeatingSystemsMetadataState = {
  openModal: false,
  openApplySettings: false,
  openAuditView: false,
  heatingSystems: [],
  heatingSystemsComponents: [],
  formData: null,
  heatingSystemsMetadata: [],
  activeHeatingSystemAtRegulationConfig: null,
  idsToApplySettings: [],
  atRegulationConfigStatusPerComponent: {},
};
const getters: GetterTree<HeatingSystemsMetadataState, RootStore> = {
  openModal: (state) => state.openModal,
  openApplySettings: (state) => state.openApplySettings,
  openAuditView: (state) => state.openAuditView,
  heatingSystems: (state) => state.heatingSystems,
  heatingSystemsComponents: (state) => state.heatingSystemsComponents,
  activeHeatingSystem: (state) => state.heatingSystems.find((item) => item.active === true),
  activeHeatingSystemComponent: (state) => state.heatingSystemsComponents.find((item) => item.active === true),
  formData: (state) => state.formData,
  heatingSystemsMetadata: (state) => state.heatingSystemsMetadata,
  idsToApplySettings: (state) => state.idsToApplySettings,
  activeHeatingSystemAtRegulationConfig: (state) => state.activeHeatingSystemAtRegulationConfig,
  atRegulationConfigStatusPerComponent: (state) => state.atRegulationConfigStatusPerComponent,
};
const mutations: MutationTree<HeatingSystemsMetadataState> = {
  openModal(state, value: boolean) {
    state.openModal = value;
  },
  openApplySettings(state, value: boolean) {
    state.openApplySettings = value;
  },
  toggleAuditView(state) {
    state.openAuditView = !state.openAuditView;
  },
  setHeatingSystems(state, value: HeatingSystem[]) {
    state.heatingSystems = value;
  },
  setHeatingSystemsComponents(state, value: HeatingSystemComponent[]) {
    state.heatingSystemsComponents = value;
  },
  setFormData(state, value: HeatingSystemMetadata) {
    state.formData = value;
  },
  setHeatingSystemMetadata(state, value: HeatingSystemMetadataResponse[]) {
    state.heatingSystemsMetadata = value;
  },
  setIdsToApplySettings(state, value: string[]) {
    state.idsToApplySettings = value;
  },
  setActiveHeatingSystemAtRegulationConfig(state, value: GetAtRegulationConfig_atRegulationConfig) {
    state.activeHeatingSystemAtRegulationConfig = value;
  },
  resetAtControlRegulationConfig(state) {
    state.activeHeatingSystemAtRegulationConfig = null;
  },
  setATRegulationConfigStatuses(state, payload: GetAtRegulationConfigStatuses_checkAtRegulationConfigStatuses[]) {
    const map: Record<string, boolean> = {};
    if (!payload || !Array.isArray(payload)) return;

    payload.forEach((item) => {
      map[item.spotId] = !!item.hasConfig;
    });

    state.atRegulationConfigStatusPerComponent = {
      ...state.atRegulationConfigStatusPerComponent,
      ...map,
    };
  },
};
const actions = {
  async getEnerIQConnection(
    { commit }: ActionContext<HeatingSystemsMetadataState, HeatingSystemsMetadataState>,
    customerId: string,
  ): Promise<Response> {
    return await fetch(`${ORIGIN_ENDPOINT}/api/v1/daedalus-connector/enerIqConnection?customerId=${customerId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${keycloak?.token ?? ''}`,
      },
    });
  },
  async createEnerIQConnection(
    { commit }: ActionContext<HeatingSystemsMetadataState, HeatingSystemsMetadataState>,
    payload: CreateConnectionPayload,
  ): Promise<Response> {
    return await fetch(`${ORIGIN_ENDPOINT}/api/v1/daedalus-connector/enerIqConnection`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${keycloak?.token ?? ''}`,
      },
      body: JSON.stringify(payload),
    });
  },
  async updateEnerIQConnection(
    { commit }: ActionContext<HeatingSystemsMetadataState, HeatingSystemsMetadataState>,
    payload: UpdateConnectionPayload,
  ): Promise<Response> {
    return await fetch(
      `${ORIGIN_ENDPOINT}/api/v1/daedalus-connector/enerIqConnection?customerId=${payload.customerId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${keycloak?.token ?? ''}`,
        },
        body: JSON.stringify({
          enabled: payload.enabled,
          consent: payload.consent,
          treeNodeIds: payload.treeNodeIds,
        }),
      },
    );
  },
  async deleteEnerIQConnection(
    { commit }: ActionContext<HeatingSystemsMetadataState, HeatingSystemsMetadataState>,
    customerId: string,
  ): Promise<Response> {
    return await fetch(`${ORIGIN_ENDPOINT}/api/v1/daedalus-connector/enerIqConnection?customerId=${customerId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${keycloak?.token ?? ''}`,
      },
    });
  },
  async loadAtControlRegulationConfig(
    { commit }: ActionContext<HeatingSystemsMetadataState, HeatingSystemsMetadataState>,
    args: { spotId: string } & ApolloClientParams,
  ): Promise<GetAtRegulationConfig_atRegulationConfig | null> {
    try {
      const { data, errors } = await args.apolloClient.query<GetAtRegulationConfig>({
        query: getAtRegulationConfigQuery,
        variables: { spotId: args.spotId },
        fetchPolicy: 'no-cache',
      });

      if (errors) {
        // eslint-disable-next-line no-console
        console.error('Error fetching AT regulation config:', errors);
        commit('setActiveHeatingSystemAtRegulationConfig', null);

        return null;
      }

      commit('setActiveHeatingSystemAtRegulationConfig', data.atRegulationConfig);
      return data.atRegulationConfig;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch AT regulation config:', error);
      commit('setActiveHeatingSystemAtRegulationConfig', null);

      return null;
    }
  },
  async activateAtControlRegulationConfig(
    { commit, dispatch }: ActionContext<HeatingSystemsMetadataState, HeatingSystemsMetadataState>,
    args: AtRegulationConfigInput & ApolloClientParams,
  ): Promise<ActivateAtRegulation_activateAtRegulation | undefined> {
    try {
      const { data, errors } = await args.apolloClient.mutate<ActivateAtRegulation, ActivateAtRegulationVariables>({
        mutation: activateAtRegulationConfigMutation,
        variables: {
          input: {
            buildingYear: args.buildingYear,
            forecastHours: args.forecastHours,
            heatedArea: args.heatedArea,
            radiatorType: args.radiatorType,
            spotId: args.spotId,
          },
        },
        fetchPolicy: 'no-cache',
      });

      if (errors) {
        const errorMessages = errors.map((error) => error.message).join(', ');
        await dispatch(ADD_TOAST_MESSAGES, {
          messages: [{ text: `Error activating configuration: ${errorMessages}`, class: 'error' }],
        });
        commit('setActiveHeatingSystemAtRegulationConfig', null);

        return undefined;
      }

      commit('setActiveHeatingSystemAtRegulationConfig', data?.activateAtRegulation ?? null);

      return data?.activateAtRegulation;
    } catch (error: unknown) {
      await dispatch(ADD_TOAST_MESSAGES, {
        messages: [{ text: `Error activating configuration: ${(error as Error).message}`, class: 'error' }],
      });
      commit('setActiveHeatingSystemAtRegulationConfig', null);

      return undefined;
    }
  },
  async deactivateAtRegulationConfigMutation(
    { commit, dispatch }: ActionContext<HeatingSystemsMetadataState, HeatingSystemsMetadataState>,
    args: DeleteAtRegulationConfigInput & ApolloClientParams,
  ): Promise<boolean> {
    try {
      const { data, errors } = await args.apolloClient.mutate<DeactivateAtRegulation, DeactivateAtRegulationVariables>({
        mutation: deActivateAtRegulationConfigMutation,
        variables: { input: { spotId: args.spotId } },
        fetchPolicy: 'no-cache',
      });

      if (errors) {
        const errorMessages = errors.map((error) => error.message).join(', ');
        await dispatch(ADD_TOAST_MESSAGES, {
          messages: [{ text: `Error deactivating configuration: ${errorMessages}`, class: 'error' }],
        });
        return false;
      }

      commit('setActiveHeatingSystemAtRegulationConfig', null);

      return data?.deactivateAtRegulation ?? false;
    } catch (error) {
      await dispatch(ADD_TOAST_MESSAGES, {
        messages: [{ text: `Error deactivating configuration: ${(error as Error).message}` }],
      });
      return false;
    }
  },
  async addHeatingSystemMetadata(
    { commit }: ActionContext<HeatingSystemsMetadataState, HeatingSystemsMetadataState>,
    payload: MetadataPayload,
  ): Promise<Response> {
    return await fetch(`${ORIGIN_ENDPOINT}/api/v1/daedalus-connector/metadata`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${keycloak?.token ?? ''}`,
      },
      body: JSON.stringify(checkPayload(payload)),
    });
  },
  async updateHeatingSystemMetadata(
    { commit }: ActionContext<HeatingSystemsMetadataState, HeatingSystemsMetadataState>,
    payload: MetadataPayload,
  ): Promise<Response> {
    return await fetch(`${ORIGIN_ENDPOINT}/api/v1/daedalus-connector/metadata?spotId=${payload.spotId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${keycloak?.token ?? ''}`,
      },
      body: JSON.stringify(checkPayload(payload)),
    });
  },
  async getHeatingSystemMetadata(
    { commit }: ActionContext<HeatingSystemsMetadataState, HeatingSystemsMetadataState>,
    id: string,
  ): Promise<Response> {
    return await fetch(`${ORIGIN_ENDPOINT}/api/v1/daedalus-connector/metadata?heatingSystemId=${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${keycloak?.token ?? ''}`,
      },
    });
  },
  async getAllHeatingSystemMetadata({
    commit,
  }: ActionContext<HeatingSystemsMetadataState, HeatingSystemsMetadataState>): Promise<Response> {
    return await fetch(`${ORIGIN_ENDPOINT}/api/v1/daedalus-connector/metadataList`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${keycloak?.token ?? ''}`,
      },
    });
  },
  async getEnerIQSuggestions(
    { commit }: ActionContext<HeatingSystemsMetadataState, HeatingSystemsMetadataState>,
    query: string,
  ): Promise<Response> {
    return await fetch(`${ORIGIN_ENDPOINT}/api/v1/daedalus-connector/enerIqSuggestions?${query}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${keycloak?.token ?? ''}`,
      },
    });
  },
  async getLatestEnerIQSuggestion(
    { commit }: ActionContext<HeatingSystemsMetadataState, HeatingSystemsMetadataState>,
    spotId: string,
  ): Promise<Response> {
    return await fetch(`${ORIGIN_ENDPOINT}/api/v1/daedalus-connector/enerIqSuggestion?spotIds=${spotId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${keycloak?.token ?? ''}`,
      },
    });
  },
  async getATRegulationConfigStatuses(
    { commit }: ActionContext<HeatingSystemsMetadataState, HeatingSystemsMetadataState>,
    args: { spotIds: string[] } & ApolloClientParams,
  ): Promise<Response> {
    try {
      const { data, errors } = await args.apolloClient.query<GetAtRegulationConfigStatuses>({
        query: getATRegulationConfigStatusesQuery,
        variables: { input: { spotIds: args.spotIds } },
        fetchPolicy: 'no-cache',
      });

      if (errors) {
        // eslint-disable-next-line no-console
        console.error('Error fetching AT regulation config statuses:', errors);
        return new Response(null, { status: 500, statusText: 'Error fetching AT regulation config statuses' });
      }

      // store statuses in state as { spotId: boolean }
      if (data && data.checkAtRegulationConfigStatuses) {
        commit('setATRegulationConfigStatuses', data.checkAtRegulationConfigStatuses);
      }

      return new Response(JSON.stringify(data), { status: 200 });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching AT regulation config statuses:', error);
      return new Response(null, { status: 500, statusText: 'Error fetching AT regulation config statuses' });
    }
  },
};

export const HeatingSystemsMetadataModule: Module<HeatingSystemsMetadataState, any> = {
  state,
  getters,
  mutations,
  actions,
};
